import React, { useEffect, useState } from 'react'
import { SecondaryBase } from './../Commons/SecondaryBase'
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage'

import { 
    CreateRunToGoalV2Match, 
    StartRunToGoalV2Match, 
    ResetRunToGoalV2Match, 
    FinishRunToGoalV2MatchEarly, 
    ShowRunToGoalV2FrontPage 
} from './../../../utilities/BL/RunToGoal'
import { ConnectWebSocketForGoalAdmin, DisconnectWebSocketForGoalAdmin } from './../../../utilities/WS/Admin/RunToGoalWS'
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'

import { SetScreenV2MainDynamic } from './../../../utilities/BL/ScreenV2'


const RunToGoalV3 = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // ==========================================================================
    // ESTADOS
    // ==========================================================================
    const [selectedSponsorTheme, setSelectedSponsorTheme] = useState('Ninguno');
    const [isSystemWorking, setIsSystemWorking] = useState(false);
    const [hasRoomBeenOpened, setHasRoomBeenOpened] = useState(false);
    const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);

    const [matchDurationSeconds, setMatchDurationSeconds] = useState(30);
    const [tapsPerTick, setTapsPerTick] = useState(10);
    const [minimumUpdateMilliseconds, setMinimumUpdateMilliseconds] = useState(100);

    const [currentMatchId, setCurrentMatchId] = useState(null);
    const [matchPhase, setMatchPhase] = useState('WAITING');
    const [websocketStatus, setWebsocketStatus] = useState('idle');

    const [liveStadiumStats, setLiveStadiumStats] = useState(null);
    const [liveZonesProgress, setLiveZonesProgress] = useState([]);
    const [currentElapsedSeconds, setCurrentElapsedSeconds] = useState(0);
    const [matchWinnerData, setMatchWinnerData] = useState(null);

    // Lista de Patrocinadores
    const sponsorsList = [
        { id: 'Ninguno', label: 'Sin Patrocinador' }
    ];

    // ==========================================================================
    // PROCESADOR WEBSOCKET
    // ==========================================================================
    const handleIncomingWebsocketMessage = (incomingMessage) => {
        const messageType = (incomingMessage?.type || '').toLowerCase();
        const payloadData = incomingMessage?.payload || {};

        switch (messageType) {
            case 'goal.created':
            case 'goal.reset':
                setCurrentMatchId(payloadData.matchId);
                setMatchPhase('WAITING');
                setLiveZonesProgress([]);
                setCurrentElapsedSeconds(0);
                setMatchWinnerData(null);
                break;

            case 'goal.started':
                setMatchPhase('RUNNING');
                setLiveZonesProgress([]);
                setCurrentElapsedSeconds(0);
                setMatchWinnerData(null);
                break;

            case 'goal.progress':
                if (payloadData.phase && payloadData.phase.toUpperCase() === 'RUNNING') {
                    setMatchPhase('RUNNING');
                }
                if (payloadData.zonesProgress) {
                    setLiveZonesProgress(payloadData.zonesProgress);
                }
                if (payloadData.elapsedMilliseconds !== undefined) {
                    setCurrentElapsedSeconds(Math.floor(payloadData.elapsedMilliseconds / 1000));
                }
                break;

            case 'goal.finished':
                setMatchPhase('FINISHED');
                if (payloadData.finalZonesProgress) {
                    setLiveZonesProgress(payloadData.finalZonesProgress);

                    const winnerString = payloadData.winner;
                    if (winnerString === 'tie') {
                        setMatchWinnerData({ text: "¡EMPATE TOTAL!", points: payloadData.finalZonesProgress[0]?.taps || 0 });
                    } else if (winnerString.startsWith('tie_partial:')) {
                        const tiedZonesList = winnerString.split(':')[1];
                        setMatchWinnerData({ text: `¡EMPATE PARCIAL! (${tiedZonesList})`, points: payloadData.finalZonesProgress.find(z => z.zone === tiedZonesList.split(' y ')[0].trim())?.taps || 0 });
                    } else {
                        const winnerZone = payloadData.finalZonesProgress.find(z => z.zone === winnerString);
                        setMatchWinnerData({ text: `ZONA ${winnerString.toUpperCase()}`, points: winnerZone?.taps || 0 });
                    }
                }
                break;

            default:
                break;
        }
    };

    useEffect(() => {
        ConnectWebSocketForGoalAdmin(handleIncomingWebsocketMessage, setWebsocketStatus);
        return () => { DisconnectWebSocketForGoalAdmin(); };
    }, []);

    useEffect(() => {
        const fetchStadiumLiveStats = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
                if (response.ok) setLiveStadiumStats(await response.json());
            } catch (error) { }
        };
        fetchStadiumLiveStats();
        const pollingInterval = setInterval(fetchStadiumLiveStats, 3000);
        return () => clearInterval(pollingInterval);
    }, []);

    // ==========================================================================
    // ACCIONES DE BOTONES
    // ==========================================================================
    
    // PASO 1: ABRIR SALA Y MOSTRAR QR
    const handleOpenRoomAndShowQR = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Limpiando estadio y mostrando QR..." });

            await BLResetCurrentDynamic(); 
            
            // 1. Mandamos la señal de Welcome (QR)
            await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, {
                method: 'POST'
            });

            // 2. Creamos la ronda en BD y purgamos los stats viejos
            const fullRoundName = `Tiro a Gol - ${selectedSponsorTheme}`;
            await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roundName: fullRoundName })
            });

            setHasRoomBeenOpened(true);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Sala abierta con éxito. QR en pantalla." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al purgar o al enviar el QR." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    // PASO 2: MOSTRAR PORTADA DEL JUEGO
    const handleShowFrontPage = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Enviando portada a pantalla..." });

            await ShowRunToGoalV2FrontPage(); // Esto avisa a la V1 (retrocompatibilidad)

            // MAGIA V2: Le decimos al Súper Lienzo que inyecte el componente RunToGoal
            await SetScreenV2MainDynamic(1, 'RunToGoal');

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Portada del juego en pantalla." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al mostrar la portada." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    // PASO 3: INICIAR JUEGO (Ya no llama a StartNewRound, por lo que NO desconecta)
    const handleStartGame = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: `Preparando carrera...` });

            const creationResponse = await CreateRunToGoalV2Match({
                matchDurationSeconds: matchDurationSeconds,
                tapsPerTick: tapsPerTick,
                minimumUpdateMilliseconds: minimumUpdateMilliseconds
            });

            if (!creationResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: creationResponse.message });
                return;
            }

            const newMatchId = creationResponse.data?.matchIdentifier;
            setCurrentMatchId(newMatchId);

            const startResponse = await StartRunToGoalV2Match(newMatchId);

            if (!startResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: startResponse.message });
                await ResetRunToGoalV2Match(newMatchId);
            } else {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `¡Carrera en curso!` });
            }

        } catch (exception) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Ocurrió un error inesperado al iniciar." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleFinishEarly = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Terminando partida..." });
            if (!currentMatchId) return;

            const finishResponse = await FinishRunToGoalV2MatchEarly(currentMatchId);
            if (!finishResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: finishResponse.message });
            }
        } catch (exception) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al terminar la partida anticipadamente." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleAbortGame = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Abortando partida..." });
            if (!currentMatchId) return;

            const resetResponse = await ResetRunToGoalV2Match(currentMatchId);
            if (!resetResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: resetResponse.message });
            } else {
                // MAGIA V2: Limpiamos el Súper Lienzo
                await SetScreenV2MainDynamic(1, null);

                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Partida abortada." });
            }
        } catch (exception) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al abortar la partida." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleReturnToDashboard = async () => {
        try {
            // MAGIA V2: Limpiamos el Súper Lienzo al salirnos de la sección
            await SetScreenV2MainDynamic(1, null);
            
            await BLResetCurrentDynamic();
            getOutOfTheDynamic();
        } catch (error) { }
    };

    const formatTime = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const seconds = (totalSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const getVisualZoneName = (realZoneName) => {
        if (!realZoneName) return '';
        const normalized = realZoneName.toLowerCase().trim();

        // if (selectedSponsorTheme === 'Daikin') {
        //     const aliases = {
        //         'laboratorio tequis': 'Pichon-kun', 'canels': 'Pichon-kun', 'seguros el potosi': 'Pichon-kun', 'nissan': 'Pichon-kun', 'lounge ron potosi': 'Pichon-kun', 'azul': 'Pichon-kun',
        //         'red cola': 'Tunita', 'tecate': 'Tunita', 'moctezuma': 'Tunita', 'mobil': 'Tunita', 'oro': 'Tunita'
        //     };
        //     return aliases[normalized] || realZoneName;
        // }

        const defaultAliases = {
            'zona1': 'Juanjolote Local',
            'zona2': 'Juanjolote Visita'
        };

        return defaultAliases[normalized] || realZoneName;
    };

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            <section className='AdmRunToGoal__Settings pb-5'>
                
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                    <p className='AdmRunToGoal__Title mb-0'>Tiro a la canasta</p>
                </div>

                <div className='AdmRunToGoal__Settings__Controls'>
                    
                    <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
                        <div className="d-flex justify-content-between align-items-center px-3">
                            <div>
                                <p className="mb-1 text-muted small">Estatus Servidor</p>
                                <p className="fw-bold mb-0">
                                    {matchPhase === 'WAITING' && <><i className="bi bi-hourglass-split"></i> En Espera</>}
                                    {matchPhase === 'RUNNING' && <><i className="bi bi-play-circle text-success"></i> ¡CARRERA EN CURSO!</>}
                                    {matchPhase === 'FINISHED' && <><i className="bi bi-stop-circle text-danger"></i> Terminada</>}
                                </p>
                            </div>

                            <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
                                <p className="mb-0 small text-uppercase" style={{ letterSpacing: '1px', color: '#aaa' }}>Cronómetro</p>
                                <h3 className="mb-0 fw-bold" style={{ fontFamily: 'monospace' }}>
                                    {formatTime(currentElapsedSeconds)} / {formatTime(matchDurationSeconds)}
                                </h3>
                            </div>
                        </div>
                    </div>

                    {/* BOTONERA TÁCTICA */}
                    <div className="mt-4">
                        {matchPhase === 'WAITING' && (
                            <>
                                {/* LISTA DE PATROCINADORES */}
                                {sponsorsList && sponsorsList.length > 0 && (
                                    <div className="mb-4 bg-light p-3 rounded border">
                                        <label className="fw-bold mb-2"><i className="bi bi-star-fill text-warning"></i> Patrocinador para esta dinámica:</label>
                                        <select 
                                            className="form-select border-primary" 
                                            value={selectedSponsorTheme} 
                                            onChange={(e) => {
                                                setSelectedSponsorTheme(e.target.value);
                                                setHasRoomBeenOpened(false);
                                            }}
                                            disabled={isSystemWorking}
                                        >
                                            {sponsorsList.map(s => (
                                                <option key={s.id} value={s.id}>{s.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* FASE 1 */}
                                <div className="row g-2 mb-4 pb-4 border-bottom">
                                    <div className="col-12 mb-2">
                                        <p className="fw-bold text-muted small mb-1">FASE 1: PREPARACIÓN</p>
                                    </div>
                                    <div className="col-sm-6">
                                        <button
                                            className={`btn btn-danger fw-bold w-100 shadow-sm py-3 ${isSystemWorking ? 'disabled' : ''}`}
                                            onClick={handleOpenRoomAndShowQR}
                                            disabled={isSystemWorking}
                                        >
                                            <i className="bi bi-qr-code"></i> 1. Mostrar QR y abrir sala
                                        </button>
                                    </div>
                                    <div className="col-sm-6">
                                        <button
                                            className={`btn btn-info fw-bold w-100 shadow-sm py-3 ${(!hasRoomBeenOpened || isSystemWorking) ? 'disabled' : ''}`}
                                            onClick={handleShowFrontPage}
                                            disabled={!hasRoomBeenOpened || isSystemWorking}
                                        >
                                            <i className="bi bi-display"></i> 2. Mostrar portada
                                        </button>
                                    </div>
                                </div>

                                {/* FASE 2: OPERACIÓN Y TIEMPO */}
                                <div className="row g-2 mb-3">
                                    <div className="col-12 mb-2 d-flex justify-content-between align-items-center">
                                        <p className="fw-bold text-muted small mb-1">FASE 2: OPERACIÓN DE CARRERA</p>
                                        
                                        <div className="d-flex align-items-center">
                                            <label className="small fw-bold me-2 mb-0">Duración:</label>
                                            <div className="input-group input-group-sm" style={{ width: '110px' }}>
                                                <input
                                                    type="number"
                                                    className="form-control text-center fw-bold"
                                                    value={matchDurationSeconds}
                                                    onChange={e => setMatchDurationSeconds(+e.target.value || 10)}
                                                />
                                                <span className="input-group-text">seg</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* BOTÓN ÚNICO DE CARRERA */}
                                    <div className="col-12">
                                        <button
                                            className={`Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons w-100 m-0 py-3 ${(!hasRoomBeenOpened || isSystemWorking) ? 'disabled' : ''}`}
                                            onClick={handleStartGame}
                                            disabled={!hasRoomBeenOpened || isSystemWorking}
                                        >
                                            <i className="bi bi-play-circle-fill"></i> Iniciar carrera
                                        </button>
                                    </div>
                                </div>

                                {/* CONFIGURACIÓN AVANZADA (TAPS) */}
                                <div className="mt-3 text-end">
                                    <button 
                                        className="btn btn-sm btn-link text-muted text-decoration-none"
                                        onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                                    >
                                        <i className={`bi bi-gear${showAdvancedConfig ? '-fill' : ''}`}></i> {showAdvancedConfig ? 'Ocultar' : 'Mostrar'} conf. avanzada
                                    </button>
                                    
                                    {showAdvancedConfig && (
                                        <div className="bg-light p-3 rounded border text-start mt-2 shadow-sm transition">
                                            <label className='small fw-bold mb-1'>Taps requeridos en celular (Batch):</label>
                                            <input
                                                className='form-control form-control-sm mb-2'
                                                style={{ maxWidth: '150px' }}
                                                type='number'
                                                min={1}
                                                value={tapsPerTick}
                                                onChange={e => setTapsPerTick(+e.target.value || 1)}
                                            />
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>Cantidad de toques que acumula el dispositivo antes de mandarlos al servidor por websocket.</small>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {matchPhase === 'RUNNING' && (
                            <button
                                className={`btn btn-warning btn-lg fw-bold w-100 mb-3 shadow-sm ${isSystemWorking ? 'disabled' : ''}`}
                                onClick={handleFinishEarly}
                                disabled={isSystemWorking}
                            >
                                <i className="bi bi-trophy-fill"></i> Terminar antes (Declarar ganador)
                            </button>
                        )}

                        {(matchPhase === 'RUNNING' || matchPhase === 'FINISHED') && (
                            <button
                                className={`btn btn-outline-danger fw-bold w-100 ${isSystemWorking ? 'disabled' : ''}`}
                                onClick={handleAbortGame}
                                disabled={!currentMatchId || isSystemWorking}
                            >
                                <i className="bi bi-x-octagon-fill"></i> {matchPhase === 'FINISHED' ? 'Limpiar y volver a espera' : 'Abortar partida (Sin ganador)'}
                            </button>
                        )}
                    </div>
                </div>

                {matchPhase === 'FINISHED' && matchWinnerData && (
                    <div className="alert alert-success mt-4 p-4 text-center shadow-lg border-0" style={{ borderRadius: '15px' }}>
                        <h6 className="text-uppercase fw-bold mb-3" style={{ letterSpacing: '2px', color: '#198754' }}>Resultado del juego</h6>
                        <h2 className="fw-black mb-2">
                            {matchWinnerData.text.startsWith('ZONA') || matchWinnerData.text.startsWith('¡EMPATE PARCIAL!') ? (
                                getVisualZoneName(matchWinnerData.text.replace('ZONA ', '').replace('¡EMPATE PARCIAL! (', '').replace(')', ''))
                            ) : (
                                matchWinnerData.text
                            )}
                        </h2>
                        <h4 className="text-muted fw-normal">con <strong className="text-dark">{matchWinnerData.points}</strong> taps</h4>
                    </div>
                )}

                {/* DASHBOARD ANALÍTICO EN VIVO */}
                <div className='mt-5 mb-5 border-top pt-4'>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">Estadísticas en Vivo</h5>
                        {liveStadiumStats && (
                            <span className="badge bg-secondary">
                                {liveStadiumStats.roundName}
                            </span>
                        )}
                    </div>

                    {liveStadiumStats ? (
                        <>
                            <div className="row text-center mb-4 g-2">
                                <div className="col">
                                    <div className="p-3 bg-light rounded border">
                                        <h2 className="fw-black text-success mb-0">{liveStadiumStats.totalCurrentlyActive}</h2>
                                        <span className="small text-muted">JUGADORES AHORA</span>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="p-3 bg-light rounded border">
                                        <h2 className="fw-black text-secondary mb-0">{liveStadiumStats.totalEverConnected}</h2>
                                        <span className="small text-muted">TOTAL (Entraron)</span>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive mb-4">
                                <table className="table table-hover table-sm border align-middle text-center bg-white shadow-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-start ps-3">ZONA (Patrocinador)</th>
                                            <th>ACTIVOS</th>
                                            <th>ENTRARON</th>
                                            <th>SALIERON</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {liveStadiumStats.zoneStats.map((z, i) => (
                                            <tr key={i}>
                                                <td className="text-start ps-3 fw-bold text-uppercase">{getVisualZoneName(z.zone)}</td>
                                                <td className="fs-6 text-success fw-bold">{z.currentActive}</td>
                                                <td>{z.totalEntered}</td>
                                                <td className="text-danger">{z.totalLeft}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="text-center p-4 text-muted">
                            <div className="spinner-border mb-3" role="status"></div>
                            <p>Cargando datos del estadio...</p>
                        </div>
                    )}

                    {/* Tabla de Taps */}
                    {liveZonesProgress.length > 0 && (
                        <div className="mt-4">
                            <h6 className="fw-bold text-muted text-uppercase mb-3"><i className="bi bi-activity text-primary"></i> Avance de Taps en Carrera</h6>
                            {liveZonesProgress.map((zoneData, index) => (
                                <div key={index} className="mb-2 bg-white p-3 rounded shadow-sm border border-start-0 border-end-0 border-4 border-primary">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <p className='fw-bold mb-0 text-uppercase'>
                                            {getVisualZoneName(zoneData.zoneIdentifier || zoneData.zone)}
                                        </p>
                                        <span className="badge bg-dark fs-5">{zoneData.totalTaps || zoneData.taps} taps</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </SecondaryBase>
    );
};

export { RunToGoalV3 };